import React from 'react';
import s from './app-card.scss';
import type {
  embeddedScriptAppStatus,
  IEmbeddedScriptAppStatus,
  Origin,
} from '../../manage-installed-apps-page';
import type { IModalProps, ModalNames } from '../../modals/modals';
import { Box } from '@wix/design-system';
import { AppIntro } from './app-intro';
import { AppCTAs } from './app-ctas';
import { AppBadge } from '../app-badge/app-badge';
import { PriceBadge } from '../price-badge/price-badge';
import type { BundleWebSolution } from '@wix/ambassador-marketplace/types';
import type { ManagedApp } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import { isBundleApp } from '../../..';

export function AppCard({
  app,
  msid,
  origin,
  baseUrl,
  editorType,
  addAppAgain,
  getAppInstance,
  embeddedScriptAppsStatus,
  updateEmbeddedScriptAppsStatus,
  deleteApp,
  mainButtonAction,
  openModal,
  isBusinessApp,
  index,
  gotoWebSolutionPage,
  siteIsPremium,
  isActionRequiredApp,
  openApp,
  deleteAppFromBundle,
  locale,
  siteIsBusinessFirstSite,
  bundleApps,
  isEditorless,
  showUserActionNotification,
  editorUrl,
  authorizationHeader,
  openDeleteModalAppInEditor,
}: {
  app: ManagedApp;
  msid: string;
  origin: Origin;
  baseUrl: string;
  editorType: string;
  isEditorless?: boolean;
  addAppAgain(appDefID: string, appVersion: string): void;
  getAppInstance(appDefId: string): string;
  embeddedScriptAppsStatus: { [key: string]: IEmbeddedScriptAppStatus };
  updateEmbeddedScriptAppsStatus(
    appDefId: string,
    newStatus: embeddedScriptAppStatus,
    appInstanceId: string,
    updateEmbeddedScriptAppsStatus: { [key: string]: IEmbeddedScriptAppStatus },
  );
  deleteApp(app: ManagedApp, showNotification: boolean): void;
  mainButtonAction(app: ManagedApp, bundleApps: BundleWebSolution[]): {} | void;
  openModal(modalName: ModalNames, modalProps: IModalProps): void;
  isBusinessApp: boolean;
  index: number;
  gotoWebSolutionPage(slug: string): void;
  siteIsPremium: boolean;
  isActionRequiredApp?: boolean;
  openApp?(app: ManagedApp): Promise<{} | void>;
  deleteAppFromBundle?(appId: string): void;
  locale?: string;
  siteIsBusinessFirstSite?: boolean;
  bundleApps: BundleWebSolution[];
  showUserActionNotification?(INotificationsOptions);
  editorUrl?: string;
  authorizationHeader?: any;
  openDeleteModalAppInEditor?(appId: string): void;
}) {
  const appId = app?.appId;
  const isBundleAppFromBundle = isBundleApp(appId, bundleApps);
  return (
    <Box
      dataHook="apps-section-app-card"
      className={s.appRow}
      align="space-between"
      verticalAlign="middle"
      height="60px"
      padding="0px 24px"
      direction="horizontal"
    >
      <Box
        direction="horizontal"
        verticalAlign="middle"
        inline={true}
        marginRight="24px"
        minWidth="242px"
        width="50%"
      >
        <AppIntro
          app={app}
          bundleApps={bundleApps}
          isActionRequiredApp={isActionRequiredApp}
          siteIsPremium={siteIsPremium}
          siteIsBusinessFirstSite={siteIsBusinessFirstSite}
        />
      </Box>
      <Box width="30%" align="left" marginRight="24px">
        {isActionRequiredApp && (
          <AppBadge
            app={app}
            appEmbeddedScriptAppsStatus={
              embeddedScriptAppsStatus &&
              embeddedScriptAppsStatus[appId]?.status
            }
            siteIsPremium={siteIsPremium}
            siteIsBusinessFirstSite={siteIsBusinessFirstSite}
            bundleApps={bundleApps}
          />
        )}
      </Box>
      <Box align="left" marginRight="24px" minWidth="11%">
        {isBundleAppFromBundle && (
          <PriceBadge
            app={bundleApps?.find((app) => app.webSolutionBase.id === appId)}
          />
        )}
      </Box>
      <Box width="342px" align="right" verticalAlign="middle">
        <Box align="right" minWidth="50%">
          <AppCTAs
            isEditorless={isEditorless}
            app={app}
            msid={msid}
            origin={origin}
            baseUrl={baseUrl}
            editorType={editorType}
            addAppAgain={addAppAgain}
            getAppInstance={getAppInstance}
            embeddedScriptAppsStatus={embeddedScriptAppsStatus}
            updateEmbeddedScriptAppsStatus={updateEmbeddedScriptAppsStatus}
            deleteApp={deleteApp}
            mainButtonAction={mainButtonAction}
            openModal={openModal}
            isBusinessApp={isBusinessApp}
            index={index}
            gotoWebSolutionPage={gotoWebSolutionPage}
            siteIsPremium={siteIsPremium}
            isActionRequiredApp={isActionRequiredApp}
            openApp={openApp}
            deleteAppFromBundle={deleteAppFromBundle}
            locale={locale}
            siteIsBusinessFirstSite={siteIsBusinessFirstSite}
            bundleApps={bundleApps}
            showUserActionNotification={showUserActionNotification}
            editorUrl={editorUrl}
            authorizationHeader={authorizationHeader}
            openDeleteModalAppInEditor={openDeleteModalAppInEditor}
          />
        </Box>
      </Box>
    </Box>
  );
}
